import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPageMomentSuggestions.module.less'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SliderModels } from '@/types/slider'
import PptPage, { PageHeader, } from '../PptPage/PptPage'
import { useSize } from 'ahooks'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  suggestions: string
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}

function PptPageMomentSuggestions({ project, onSliderReady, no, suggestions, }: Props) {
  const { t } = useTranslation()
  const BOX_MAX_HEIGHT = 600
  const [ratio, setRatio] = useState<number>(1)
  const [ratioFinished, setRatioFinished] = useState<boolean>(false)
  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const emailRef = useRef<HTMLImageElement>(null)
  const titleRef = useRef<HTMLSpanElement>(null)
  const suggestionRef = useRef<HTMLDivElement>(null)
  const [suggestionChildren, setSuggestionChildren] = useState<HTMLCollection>()
  const placeholderRef = useRef<HTMLImageElement>(null)

  const suggestionSize = useSize(suggestionRef.current)

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }

  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  useEffect(() => {
    if (
      !suggestionChildren ||
      !emailRef.current ||
      !titleRef.current ||
      !placeholderRef.current ||
      !suggestionRef.current
    ) {
      return
    }
    const emailRect = emailRef.current.getBoundingClientRect()
    const titleRect = titleRef.current.getBoundingClientRect()
    const placeholderRect = placeholderRef.current.getBoundingClientRect()
    const suggestionRect = suggestionRef.current.getBoundingClientRect()
    // 20 是 border-radius
    const SHAPE_RATIO = 20 / suggestionRect.height
    // 因为各个方块的大小高度其实是一致的，所以这里统一设置
    const SHAPE_WIDTH = suggestionRect.width
    const SHAPE_HEIGHT = suggestionRect.height
    const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
    const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
    const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO

    const elements: SliderModels.PPTElement[] = [
      {
        type: 'image',
        src: emailRef.current.src,
        x: emailRect.x,
        y: emailRect.y,
        width: emailRect.width,
        height: emailRect.height,
      },
      {
        type: 'text',
        content: titleRef.current.outerHTML,
        x: titleRect.x + 20,
        y: titleRect.y - 60,
        width: titleRect.width,
        height: titleRect.height,
      },
      {
        type: 'shape',
        x: suggestionRect.x,
        y: suggestionRect.y,
        width: suggestionRect.width,
        height: suggestionRect.height + suggestionChildren.length * 30,
        viewBox: [suggestionRect.width, suggestionRect.height],
        path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
        fill: '#f0f0ff',
        fixedRatio: false,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [SHAPE_RATIO],
        // NOTE: 这里注释掉是因为 pptxgenjs 不支持 gradient 的设置
        // gradient: {
        //   type: 'linear',
        //   rotate: 22,
        //   colors: [
        //     { pos: 0, color: '#F4F4FE' },
        //     { pos: 40, color: '#D9D8FF' },
        //     { pos: 100, color: '#F6F6FF' },
        //   ],
        // },
        outline: {
          width: 1,
          color: '#D5D4FC',
          style: 'solid',
        },
      },
      ...Array.from(suggestionChildren).map((el) => {
        const rect = el.getBoundingClientRect()
        return {
          type: 'text',
          content: el.outerHTML.replace(/\n/g, ''),
          x: rect.x,
          y: rect.y - 30,
          width: rect.width,
          height: rect.height,
        } as SliderModels.PPTTextElement
      }),
      {
        type: 'image',
        src: placeholderRef.current.src,
        x: placeholderRect.x,
        y: placeholderRect.y,
        width: placeholderRect.width,
        height: placeholderRect.height,
      },
    ]
    setElements(elements)
  }, [suggestionChildren,])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  useEffect(() => {
    if (!ratioFinished || !suggestionRef.current || !project) {
      return
    }
    const suggestionElement = suggestionRef.current
    const children = suggestionElement.children
    for (const child of children) {
      const c = child as HTMLElement
      c.style.fontSize = `${24 * ratio}px`
      c.style.color = '#2A2C3F'
    }
    setSuggestionChildren(children)
  }, [project, ratio, ratioFinished])


  // useEffect(() => {
  //   if (suggestionRef.current) {
  //     const suggestionHeight = suggestionRef.current.getBoundingClientRect().height
  //     if (suggestionHeight > BOX_MAX_HEIGHT) {
  //       const ratio = BOX_MAX_HEIGHT / suggestionHeight
  //       setRatio(ratio)
  //     }
  //     setRatioFinished(true)
  //   }
  // }, [])

  useEffect(() => {
    if (suggestionSize) {
      const suggestionHeight = suggestionSize.height
      if (suggestionHeight > BOX_MAX_HEIGHT) {
        setRatio((ratio) => ratio - 0.1)
      } else {
        setRatioFinished(true)
      }
    }
  }, [suggestionSize])


  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-8.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPageMomentSuggestions}>
        <PageHeader
          project={project}
          text={'03.' + t('dashboard.projectDetailPage.detailAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.content}>
          <div className={styles.left}>
            <p className={styles.title}>
              <img
                ref={emailRef}
                className={styles.icon}
                style={{ width: 46, height: 46 }}
                src={commonUtils.getImagePath('PDFDownload/pdf-page-10/page10-icon.png')}
              />
              <span ref={titleRef} style={{ fontSize: 36, color: '#6D54FA', fontWeight: 'bold' }}>
                {t('dashboard.projectDetailPage.storyboardView.suggestions')}
              </span>
            </p>
            <div className={styles.summary} ref={suggestionRef}>
              <MarkdownRenderer
                style={{ fontSize: 24 * ratio }}
                markdownText={suggestions}
              />
            </div>
          </div>

          <div className={styles.right}>
            <img
              ref={placeholderRef}
              style={{ width: 396, height: 709 }}
              className={styles.img}
              src={commonUtils.getImagePath('PPTDownload/right-placeholder-2.png')}
            />
          </div>
        </div>
      </div>
    </PptPage>
  )
}

export default PptPageMomentSuggestions
